import { Link } from "react-router-dom";

type Props = {
  id: string;
  productId: number;
  icon: string;
  title: string;
  tagline: string;
  description: string;
  features: string[];
  startingPrice: number;
  color: string;
  imageUrl: string;
  reverse?: boolean;
  badge?: string;
};

export default function ServiceDetailRow({
  id,
  productId,
  icon,
  title,
  tagline,
  description,
  features,
  startingPrice,
  color,
  imageUrl,
  reverse,
  badge,
}: Props) {
  return (
    <article
      id={id}
      className="scroll-mt-24 py-16 md:py-24 border-t border-ink-100 first:border-t-0"
      data-product-shop
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        {/* Image */}
        <div className={`lg:col-span-6 ${reverse ? "lg:order-2" : ""}`}>
          <Link
            to={`/site/services/${productId}`}
            className="block relative rounded-2xl overflow-hidden h-[360px] md:h-[480px] cursor-pointer"
          >
            <img src={imageUrl} alt={title} className="w-full h-full object-cover object-top" />
            <div className={`absolute top-5 left-5 w-14 h-14 flex items-center justify-center rounded-xl bg-gradient-to-br ${color} text-white`}>
              <i className={`${icon} text-2xl`}></i>
            </div>
            {badge && (
              <div className="absolute top-5 right-5 px-3 py-1.5 rounded-full bg-white/95 backdrop-blur text-[11px] font-bold text-ink-900 uppercase tracking-wider">
                {badge}
              </div>
            )}
          </Link>
        </div>

        {/* Copy */}
        <div className={`lg:col-span-6 ${reverse ? "lg:order-1" : ""}`}>
          <div className="text-xs uppercase tracking-[0.18em] text-brand-600 font-bold mb-3">
            {tagline}
          </div>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-ink-900 leading-tight tracking-tight mb-5">
            <Link to={`/site/services/${productId}`} className="hover:text-brand-700 cursor-pointer">
              {title}
            </Link>
          </h2>
          <p className="text-base text-ink-600 leading-relaxed mb-7">{description}</p>

          <ul className="space-y-3 mb-8">
            {features.map((f) => (
              <li key={f} className="flex items-start gap-3 text-sm text-ink-700">
                <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-brand-100 text-brand-600 mt-0.5">
                  <i className="ri-check-line text-sm"></i>
                </span>
                <span>{f}</span>
              </li>
            ))}
          </ul>

          <div className="flex flex-col sm:flex-row sm:items-center gap-4 pt-6 border-t border-ink-100">
            <div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-ink-400 font-semibold">
                Starting at
              </div>
              <div className="font-display text-3xl font-bold text-ink-900">
                ${startingPrice}
                <span className="text-sm font-semibold text-ink-500 ml-1">/ project</span>
              </div>
            </div>
            <div className="ml-auto flex flex-col sm:flex-row gap-3">
              <Link
                to="/site/contact"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md text-sm font-semibold border border-ink-200 hover:border-ink-300 hover:bg-cream-100 text-ink-800 cursor-pointer whitespace-nowrap transition-colors"
              >
                Get a Quote
              </Link>
              <Link
                to={`/site/services/${productId}`}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md text-sm font-semibold bg-ink-900 hover:bg-ink-800 text-white cursor-pointer whitespace-nowrap transition-colors"
              >
                Book Now
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}